import React, { useState } from 'react';
import axios from 'axios';
import { Mail, CheckCircle2, Loader2, BellRing } from 'lucide-react';

const API = '/api';
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Bloc de capture d'e-mail en fin d'article — convertit le trafic SEO
 * en prospects / médecins inscrits. Stocke le lead côté backend.
 */
export const NewsletterSignup = ({ source }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const val = email.trim();
    if (!EMAIL_RE.test(val)) {
      setError('Veuillez saisir une adresse e-mail valide.');
      return;
    }
    setStatus('loading');
    try {
      await axios.post(`${API}/blog/subscribe`, { email: val, source: source || '' });
      setStatus('done');
    } catch (err) {
      setStatus('idle');
      setError("Une erreur est survenue. Merci de réessayer.");
    }
  };

  if (status === 'done') {
    return (
      <div
        data-testid="newsletter-success"
        className="mt-12 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center"
      >
        <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
        <p className="text-white font-semibold" style={{ fontFamily: 'Outfit, Manrope, system-ui, sans-serif' }}>
          Merci, c'est noté !
        </p>
        <p className="text-sm text-slate-300 mt-1">
          Vous serez informé(e) des nouvelles fiches documentaires. En attendant, créez votre compte pour accéder à l'assistant.
        </p>
        <a
          href="/login"
          data-testid="newsletter-create-account"
          className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium px-5 py-2.5 transition-colors"
        >
          Créer mon compte
        </a>
      </div>
    );
  }

  return (
    <div
      data-testid="newsletter-signup"
      className="mt-12 rounded-2xl border border-slate-700 bg-gradient-to-br from-slate-900/70 to-slate-950 p-6"
    >
      <div className="flex items-center gap-2 mb-2 text-indigo-300">
        <BellRing className="w-4 h-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">Restez à jour</span>
      </div>
      <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, Manrope, system-ui, sans-serif' }}>
        Recevez les nouvelles fiches documentaires
      </h2>
      <p className="text-sm text-slate-300 mt-1.5 mb-4">
        Une sélection de synthèses sourcées pour votre pratique. Pas de spam, désinscription en un clic.
      </p>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2.5">
        <div className="relative flex-1">
          <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="medecin@exemple.fr"
            data-testid="newsletter-email-input"
            className="w-full rounded-xl bg-slate-900 border border-slate-700 focus:border-indigo-500 focus:outline-none text-white text-sm pl-10 pr-3 py-3 min-h-[44px] placeholder:text-slate-600"
          />
        </div>
        <button
          type="submit"
          disabled={status === 'loading'}
          data-testid="newsletter-submit"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-medium px-5 py-3 min-h-[44px] disabled:opacity-60 transition-colors"
        >
          {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          S'inscrire
        </button>
      </form>
      {error && <p className="text-xs text-red-400 mt-2" data-testid="newsletter-error">{error}</p>}
      <p className="text-[11px] text-slate-500 mt-3">
        En vous inscrivant, vous acceptez de recevoir nos communications. Réservé aux professionnels de santé.
      </p>
    </div>
  );
};

export default NewsletterSignup;
